import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent, Dialog, DialogContent } from '@guildeducationinc/recess';
import FileCopyIcon from '@guildeducationinc/recess/icons/FileCopyIcon';
import ReplayRoundedIcon from '@guildeducationinc/recess/icons/ReplayRoundedIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav/AdminTopNav';
import { ImportError } from '@src/components/Admin/ImportError/ImportError';
import { RunsFilter } from '@src/components/Admin/RunsFilter/RunsFilter';
import { REPLAY_IMPORT_RUN } from '@src/graphql/queries/import';
import { useClientIdParam } from '@src/hooks/useClientIdParam';
import { useClients } from '@src/hooks/useClients';
import { useImportRuns } from '@src/hooks/useImportRuns';
import { ImportStatus } from '@src/types';
import { DataTable, Messages, SearchFilterBar, TrackableButton, useLocale, useSessionState, } from 'pe-core-frontend';
import { useState } from 'react';
export const ImportRuns = () => {
    const { t, formatToLocalDatetime } = useLocale();
    const clientId = useClientIdParam();
    const [importError, setImportError] = useState(undefined);
    // A parent client's runs list includes its children's runs, so the client name is what
    // tells them apart. On a client with no children every row would repeat the same name.
    const { find } = useClients({ id: clientId, skip: !clientId });
    const client = clientId ? find(clientId) : undefined;
    // RunsFilter saves the status, startDate, and endDate in the session storage, too. We need to get
    // them from the session storage here so the filter will be automatically applied when this component mounts.
    const [status, setStatus] = useSessionState('ImportRunsStatus', '');
    const [startDate, setStartDate] = useSessionState('ImportRunsStartDate', '');
    const [endDate, setEndDate] = useSessionState('ImportRunsEndDate', '');
    const [messages, setMessages] = useState([]);
    const [replayImportRun] = useMutation(REPLAY_IMPORT_RUN);
    const { importRuns, loading, error, page, pageSize, setPage, setPageSize, orderBy, setOrderBy, orderByDir, setOrderByDir, total, refetch, } = useImportRuns({
        clientId,
        status,
        startDate,
        endDate,
    });
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleShowErrors = (row) => {
        setImportError(row.error);
    };
    const handleFilterChange = (filter) => {
        setStatus(filter.status);
        setStartDate(filter.startDate);
        setEndDate(filter.endDate);
    };
    const handleReplay = async (row) => {
        try {
            const result = await replayImportRun({
                variables: {
                    id: row.id,
                },
            });
            if (result.data?.replayImportRun && result.data.replayImportRun.success) {
                await refetch();
            }
            setMessages(result.data.replayImportRun.messages);
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Unable to replay import run'));
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Imports'), searchFilterActions: _jsx(SearchFilterBar, { enableFilter: true, filterActions: _jsx(RunsFilter, { type: 'import', onChange: handleFilterChange }), isFilterActive: !!(status || startDate || endDate) }) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(Messages, { messages: messages }), _jsx(DataTable, { loading: loading, disableColumnMenu: true, disableRowSelectionOnClick: true, rows: importRuns || [], pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, rowCount: total, sortingMode: 'server', onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'id', sort: orderByDir }], columns: [
                                    {
                                        field: 'importName',
                                        headerName: t('Import Name'),
                                        flex: 0.2,
                                        sortable: true,
                                    },
                                    ...(client?.isParent
                                        ? [
                                            {
                                                field: 'clientName',
                                                headerName: t('Client'),
                                                flex: 0.2,
                                                sortable: false,
                                            },
                                        ]
                                        : []),
                                    {
                                        field: 'status',
                                        headerName: t('Status'),
                                        flex: 0.2,
                                        sortable: true,
                                        renderCell: ({ value }) => t(`ImportStatus.${value}`),
                                    },
                                    {
                                        field: 'numRowsImported',
                                        headerName: t('Imported'),
                                        flex: 0.1,
                                        sortable: true,
                                    },
                                    {
                                        field: 'totalRows',
                                        headerName: t('Total'),
                                        flex: 0.1,
                                        sortable: true,
                                    },
                                    {
                                        field: 'startedAt',
                                        headerName: t('Started'),
                                        flex: 0.2,
                                        sortable: true,
                                        renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'short', timeStyle: 'medium' }),
                                    },
                                    {
                                        field: 'completedAt',
                                        headerName: t('Completed'),
                                        flex: 0.2,
                                        sortable: true,
                                        renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'short', timeStyle: 'medium' }),
                                    },
                                    {
                                        field: 'action',
                                        headerName: t('Actions'),
                                        renderCell: ({ row }) => (_jsx("div", { children: row.error && (_jsxs(_Fragment, { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Show errors', tag: `import_run_${row.id}`, label: t('Show errors'), appearance: 'minimal', icon: _jsx(FileCopyIcon, { size: 20 }), onClick: () => handleShowErrors(row), className: 'margin--right_8' }), row.status === ImportStatus.QUARANTINE && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Replay', tag: `import_run_${row.id}`, label: t('Replay'), appearance: 'minimal', icon: _jsx(ReplayRoundedIcon, { size: 20 }), onClick: () => handleReplay(row), className: 'margin--right_8' }))] })) })),
                                        width: 100,
                                        sortable: false,
                                    },
                                ] }), _jsx(Dialog, { isOpen: importError !== undefined, title: t('Import Errors'), onClose: () => setImportError(undefined), size: 'xl', forceDisableFocusTrap: true, children: _jsx(DialogContent, { className: 'max-h-dvh', children: _jsx(ImportError, { error: importError || '' }) }) })] }) }) })] }));
};
